import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import cn from "classnames";
import { Metric } from "../../../cores/boardAnalytics/interface/api/get-insights";
import { CL$Format } from "../../../cores/utils";

interface Props {
  summary?: Metric;
  currency?: string;
}

const KPI_KEYS = [
  "totalOpportunities",
  "openDeals",
  "wonDeals",
  "lostDeals",
  "pipelineValue",
  "wonValue",
  "avgDealSize",
  "winRate",
  "expiringSoon",
  "overdue",
] as const;

const KPI_VARIANTS: Record<string, string> = {
  totalOpportunities: "total",
  openDeals: "open",
  wonDeals: "won",
  lostDeals: "lost",
  pipelineValue: "pipeline",
  wonValue: "wonValue",
  avgDealSize: "avgDealSize",
  winRate: "winRate",
  expiringSoon: "expiringSoon",
  overdue: "overdue",
};

export const CrmKpiRow = ({ summary, currency }: Props) => {
  const intl = useIntl();

  const valuesByKey = useMemo(() => {
    if (!summary?.values) {
      return {};
    }

    return summary.values.reduce<Record<string, number>>((acc, item) => {
      if (typeof item.value === "number") {
        acc[item.label] = item.value;
      }
      return acc;
    }, {});
  }, [summary]);

  if (!summary?.values?.length) {
    return null;
  }

  const formatValue = (key: string, value: number) => {
    if (["pipelineValue", "wonValue", "avgDealSize"].includes(key)) {
      return CL$Format(value);
    }
    if (key === "winRate") {
      return `${value}%`;
    }
    return value;
  };

  return (
    <div className="crm-kpi-row">
      {KPI_KEYS.map((key) => {
        const value = valuesByKey[key];
        if (value === undefined) {
          return null;
        }

        return (
          <div
            key={key}
            className={cn(
              "crm-kpi-card",
              `crm-kpi-card--${KPI_VARIANTS[key]}`
            )}
          >
            <div className="crm-kpi-card__value">
              {formatValue(key, value)}
            </div>
            <div className="crm-kpi-card__label">
              {intl.formatMessage({ id: `app.metricsCrm.kpi.${key}` })}
            </div>
            {currency &&
              ["pipelineValue", "wonValue", "avgDealSize"].includes(key) && (
                <div className="crm-kpi-card__meta">{currency}</div>
              )}
          </div>
        );
      })}
    </div>
  );
};
